import { useQuery } from "@tanstack/react-query";
import { searchDataStudioAssets } from "../services/dataStudio/dataStudioTablesApi";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type {
  ListDataStudioTablesRequest,
  ListDataStudioTablesResponse,
} from "../services/dataStudio/dataStudioTables.types";

/** Panel page size. The API caps a page at 200. */
export const DATA_STUDIO_ASSET_PAGE_SIZE = 50;

export interface UseDataStudioAssetSearchResult {
  data: ListDataStudioTablesResponse | undefined;
  isLoading: boolean;
  /** True while a new search term is being fetched over existing results. */
  isFetching: boolean;
  errorMessage: string | null;
}

/**
 * Stable cache identity for a search request. The request object is rebuilt on
 * every render, so the key has to be derived from its values — not the object.
 */
export const serializeAssetSearchRequest = (
  request: ListDataStudioTablesRequest,
): string =>
  JSON.stringify({
    search: request.search?.trim() ?? "",
    assetTypes: request.assetTypes ?? null,
    connectionIds: request.connectionIds ?? null,
    limit: request.limit ?? DATA_STUDIO_ASSET_PAGE_SIZE,
    offset: request.offset ?? 0,
    sortOrder: request.sortOrder ?? "asc",
  });

/**
 * Tenant-wide asset search behind the ingested-tables panel.
 *
 * Searching and sorting are server-side, so the panel searches the whole
 * tenant rather than filtering whichever page it happens to hold.
 * `placeholderData` keeps the previous results on screen while a new term is
 * in flight, so the list does not blank out on every keystroke.
 */
export const useDataStudioAssetSearch = (
  request: ListDataStudioTablesRequest = {},
): UseDataStudioAssetSearchResult => {
  const query = useQuery<ListDataStudioTablesResponse>({
    queryKey: queryKeys.dataStudio.assetSearch(
      serializeAssetSearchRequest(request),
    ),
    queryFn: () =>
      searchDataStudioAssets({
        limit: DATA_STUDIO_ASSET_PAGE_SIZE,
        ...request,
      }),
    placeholderData: (previous) => previous,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    errorMessage: query.isError
      ? getApiErrorMessage(query.error, "Unable to load ingested tables.")
      : null,
  };
};
